import React, { useState } from 'react';
import { Heart, MessageCircle, Bookmark, Play, Lock, BellPlus, Eye } from 'lucide-react';
import { ResponsiveImage } from './ResponsiveImage';
export interface AngelPost {
  id: string;
  angelId: string;
  angelName: string;
  angelUsername: string;
  angelAvatar: string;
  image: string;
  caption: string;
  likes: number;
  comments: number;
  timestamp: string;
  isLive: boolean;
  isPremium: boolean;
  isVideo: boolean;
  badges: string[];
  size: 'small' | 'medium' | 'large' | 'wide' | 'tall';
}
interface AngelTileProps {
  post: AngelPost;
  onOpenPost: (post: AngelPost) => void;
  gridSize?: 'compact' | 'standard' | 'immersive';
}
export const AngelTile: React.FC<AngelTileProps> = ({
  post,
  onOpenPost,
  gridSize = 'standard'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Calculate tile classes based on size and grid layout
  const getTileClasses = () => {
    // Base classes for all grid sizes
    let baseClasses = '';
    // Adjust based on grid size preference
    if (gridSize === 'compact') {
      return baseClasses; // No span modifications for compact view
    }
    // For standard and immersive views, apply size-based spans
    switch (post.size) {
      case 'small':
        return `${baseClasses}`;
      case 'medium':
        return `${baseClasses} ${gridSize === 'immersive' ? 'md:col-span-1 md:row-span-1' : ''}`;
      case 'large':
        return `${baseClasses} ${gridSize === 'immersive' ? 'md:col-span-2 md:row-span-2' : 'md:col-span-2 md:row-span-1'}`;
      case 'wide':
        return `${baseClasses} ${gridSize === 'immersive' ? 'md:col-span-2 md:row-span-1' : ''}`;
      case 'tall':
        return `${baseClasses} ${gridSize === 'immersive' ? 'md:col-span-1 md:row-span-2' : ''}`;
      default:
        return baseClasses;
    }
  };
  // Get aspect ratio based on post size and grid size
  const getAspectRatio = () => {
    if (gridSize === 'compact') return '1/1'; // Square for compact
    switch (post.size) {
      case 'wide':
        return '16/9';
      case 'tall':
        return '3/4';
      case 'large':
        return gridSize === 'immersive' ? '4/5' : '3/4';
      default:
        return '3/4';
      // Default aspect ratio
    }
  };
  // Time ago helper function
  const timeAgo = (timestamp: string) => {
    // Simple time ago function
    const now = new Date();
    const postDate = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - postDate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };
  return <div className={`${getTileClasses()} relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 ${isHovered ? 'shadow-xl shadow-pink-600/10 scale-[0.98]' : 'shadow-md'}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={() => onOpenPost(post)} data-id="element-21618">
      {/* Image container with improved responsive image */}
      <div className="w-full h-full relative bg-gray-800 overflow-hidden" style={{
      aspectRatio: getAspectRatio()
    }} data-id="element-21619">
        <ResponsiveImage src={post.image} alt={post.caption} className="w-full h-full transition-transform duration-500" aspectRatio={getAspectRatio()} sizes={gridSize === 'compact' ? '(max-width: 640px) 50vw, (max-width: 768px) 33vw, 20vw' : gridSize === 'standard' ? '(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw' : '(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw'} loadingEffect="blur" data-id="element-21620" />
        {/* Hover overlay with enhanced styling */}
        {isHovered && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-between p-3 transition-opacity duration-300 animate-in fade-in" data-id="element-21621">
            {/* Top section with angel info */}
            <div className="flex items-center gap-2" data-id="element-21622">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20" data-id="element-21623">
                <img src={post.angelAvatar} alt={post.angelName} className="w-full h-full object-cover" data-id="element-21624" />
              </div>
              <div data-id="element-21625">
                <p className="text-white text-sm font-medium" data-id="element-21626">
                  {post.angelName}
                </p>
                <p className="text-white/70 text-xs" data-id="element-21627">
                  {timeAgo(post.timestamp)}
                </p>
              </div>
            </div>
            {/* Action buttons (new) */}
            <div className="flex justify-center gap-2 mb-4" data-id="element-21628">
              <button className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-2 transition-all duration-200 hover:scale-110" data-id="element-21629">
                <Heart size={18} className="text-pink-500" data-id="element-21630" />
              </button>
              <button className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-2 transition-all duration-200 hover:scale-110" data-id="element-21631">
                <BellPlus size={18} className="text-blue-500" data-id="element-21632" />
              </button>
              <button className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-2 transition-all duration-200 hover:scale-110" data-id="element-21633">
                <Eye size={18} className="text-purple-500" data-id="element-21634" />
              </button>
            </div>
            {/* Bottom section with likes and comments */}
            <div className="flex items-center gap-4" data-id="element-21635">
              <div className="flex items-center gap-1" data-id="element-21636">
                <Heart size={16} className="text-white" data-id="element-21637" />
                <span className="text-white text-sm" data-id="element-21638">{post.likes}</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-21639">
                <MessageCircle size={16} className="text-white" data-id="element-21640" />
                <span className="text-white text-sm" data-id="element-21641">{post.comments}</span>
              </div>
            </div>
          </div>}
        {/* Badges and indicators (always visible) - enhanced styling */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1" data-id="element-21642">
          {post.isLive && <div className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold shadow-lg shadow-red-500/30 animate-pulse" data-id="element-21643">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" data-id="element-21644"></span>
              <span data-id="element-21645">LIVE</span>
            </div>}
          {post.badges.map((badge, index) => <div key={index} className={`text-white text-xs px-2 py-0.5 rounded-full font-semibold shadow-md ${badge === 'Top Angel' ? 'bg-gradient-to-r from-yellow-500 to-amber-500 shadow-yellow-500/30' : badge === 'Trending' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-blue-500/30' : 'bg-gradient-to-r from-pink-500 to-purple-500 shadow-pink-500/30'}`} data-id="element-21646">
              {badge}
            </div>)}
        </div>
        {/* Premium or video indicator - enhanced styling */}
        {post.isPremium && <div className="absolute bottom-2 right-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold shadow-md shadow-pink-500/30" data-id="element-21647">
            <Lock size={12} data-id="element-21648" />
            <span data-id="element-21649">Premium</span>
          </div>}
        {post.isVideo && !post.isPremium && <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold" data-id="element-21650">
            <Play size={12} fill="currentColor" data-id="element-21651" />
            <span data-id="element-21652">Video</span>
          </div>}
      </div>
    </div>;
};